import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const API = '/api';

interface ApiloNote {
  id: number | string | null;
  type: number | null;
  comment: string;
  created_at: string | null;
  source?: 'apilo' | 'local';
  author_name?: string | null;
}

// Wspolny guzik "Apilo" - pokazuje wiadomosci Apilo (orderNotes) + NASZE lokalne adnotacje w popupie.
// Uzywany w wynikach wyszukiwania (ApiloModule). orderId = id_external (UUID).
// Pole dodawania zapisuje lokalnie (NIE do Apilo). Popup jest PRZESUWALNY (chwyt za naglowek).
export default function ApiloNotesButton({
  orderId,
  variant = 'desktop',
}: {
  orderId: string;
  variant?: 'desktop' | 'mobile' | 'inline';
}) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState<ApiloNote[] | null>(null);
  const [errMsg, setErrMsg] = useState('');
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [syncMsg, setSyncMsg] = useState('');
  const [statusMap, setStatusMap] = useState<{id:number,name:string}[]>([]);
  const [curStatus, setCurStatus] = useState<number | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [doc, setDoc] = useState<{has_document:boolean,type_name?:string|null,number?:string|null} | null>(null);

  // przesuwanie okna: offset wzgledem srodka ekranu
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ active: boolean; startX: number; startY: number; baseX: number; baseY: number; moved: boolean }>({
    active: false, startX: 0, startY: 0, baseX: 0, baseY: 0, moved: false,
  });

  const fmtDate = (d: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('pl-PL'); } catch { return d; }
  };

  const load = useCallback(async () => {
    setErrMsg('');
    setLoading(true);
    setNotes(null);
    setCurStatus(null);
    try {
      const res = await axios.get(`${API}/apilo/order-notes?order_id=${encodeURIComponent(orderId)}`);
      const status = res.data?.status;
      const list: ApiloNote[] = res.data?.notes || [];
      if (status === 'not_configured') {
        setErrMsg('Brak konfiguracji Apilo dla tej firmy.');
      } else if (status === 'not_found') {
        setErrMsg('Nie znaleziono tego zam\u00f3wienia w Apilo.');
      } else if (status === 'api_error') {
        setErrMsg('Apilo chwilowo niedost\u0119pne. Spr\u00f3buj ponownie za chwil\u0119.');
      } else {
        setNotes(list);
        const sid = res.data?.order_status_id;
        setCurStatus(typeof sid === 'number' ? sid : null);
      }
    } catch (e) {
      setErrMsg('B\u0142\u0105d pobierania wiadomo\u015bci z Apilo.');
    }
    setLoading(false);
  }, [orderId]);
  const loadStatusDoc = useCallback(async () => {
    try {
      const [sm, od] = await Promise.all([
        axios.get(`${API}/apilo/order-status-map`),
        axios.get(`${API}/apilo/order-document?order_id=${encodeURIComponent(orderId)}`),
      ]);
      setStatusMap(sm.data?.statuses || []);
      setDoc(od.data || null);
    } catch (e) { setStatusMap([]); setDoc(null); }
  }, [orderId]);
  const changeStatus = async (sid: number) => {
    setStatusSaving(true); setStatusMsg('');
    try {
      await axios.put(`${API}/apilo/order-status`, { order_id: orderId, status_id: sid });
      setCurStatus(sid); setStatusMsg('\u2713 Status zmieniony');
      setTimeout(() => setStatusMsg(''), 3000);
    } catch (e:any) { setStatusMsg('Nie uda\u0142o si\u0119 zmieni\u0107 statusu'); }
    setStatusSaving(false);
  };

  const handleClick = async () => {
    if (!orderId || loading) return;
    loadStatusDoc();
    setPos({ x: 0, y: 0 });
    setOpen(true);
    await load();
  };

  const addNote = async () => {
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);
    setSaveErr('');
    try {
      const _resp = await axios.post(`${API}/apilo/order-notes/local`, { order_id: orderId, comment: text });
      setDraft('');
      const _synced = _resp && _resp.data && _resp.data.apilo_synced;
      setSyncMsg(_synced ? '\u2713 Zapisano w CRM i wys\u0142ano do Apilo' : '\u2713 Zapisano w CRM (nie wys\u0142ano do Apilo)');
      setTimeout(() => setSyncMsg(''), 4000);
      await load();
    } catch (e) {
      setSaveErr('Nie uda\u0142o si\u0119 zapisa\u0107 adnotacji.');
    }
    setSaving(false);
  };

  // przeciaganie: naglowek -> onMouseDown, globalne mousemove/mouseup
  const onHeaderMouseDown = (e: React.MouseEvent) => {
    dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, baseX: pos.x, baseY: pos.y, moved: false };
    e.preventDefault();
  };

  useEffect(() => {
    if (!open) return;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d.active) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) d.moved = true;
      setPos({ x: d.baseX + dx, y: d.baseY + dy });
    };
    const onUp = () => { dragRef.current.active = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [open]);

  const btnStyle: React.CSSProperties =
    variant === 'inline'
      ? { fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid #ddd6fe', background: '#f5f3ff', color: '#6d28d9', cursor: 'pointer', whiteSpace: 'nowrap' }
      : variant === 'mobile'
      ? { fontSize: 12, padding: '4px 10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(196,181,253,0.3)', color: '#fff', cursor: 'pointer', fontWeight: 500, marginLeft: 6 }
      : { padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, background: open ? '#6d28d9' : '#f5f3ff', color: open ? '#fff' : '#6d28d9', border: '1px solid #ddd6fe' };

  return (
    <>
      <button onClick={handleClick} disabled={loading || !orderId} style={btnStyle} title={"Wiadomo\u015bci z Apilo dodane do tego zam\u00f3wienia"}>
        {loading ? '\u23f3' : (variant === 'mobile' ? '\ud83d\udcdd' : '\ud83d\udcdd Apilo - CRM')}
      </button>
      {open && (
        <div
          onClick={() => { if (!dragRef.current.moved) setOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, maxWidth: 560, width: '100%', maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', transform: `translate(${pos.x}px, ${pos.y}px)`, display: 'flex', flexDirection: 'column' }}
          >
            {/* naglowek = uchwyt do przesuwania */}
            <div
              onMouseDown={onHeaderMouseDown}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px 12px', cursor: 'move', userSelect: 'none', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}
            >
              <div style={{ fontWeight: 600, fontSize: 16 }}>{"\u2725 \ud83d\udcdd Wiadomo\u015bci z Apilo / CRM"}</div>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>{'\u2715'}</button>
            </div>

            <div style={{ padding: 20, overflow: 'auto' }}>
              {/* status zamowienia + dokument sprzedazy */}
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid #ede9fe' }}>
                {statusMap.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>Status Apilo:</span>
                    <select value={curStatus ?? ''} onChange={e => changeStatus(Number(e.target.value))} disabled={statusSaving}
                      style={{ fontSize: 12, padding: '3px 8px', borderRadius: 6, border: '1px solid #ddd6fe', background: '#fff', color: '#4c1d95', cursor: 'pointer' }}>
                      <option value="" disabled>{statusSaving ? 'Zapisuj\u0119\u2026' : 'wybierz status\u2026'}</option>
                      {statusMap.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      {curStatus !== null && !statusMap.some(s => s.id === curStatus) && (
                        <option value={curStatus}>Status #{curStatus}</option>
                      )}
                    </select>
                    {statusMsg && <span style={{ fontSize: 11, color: statusMsg.startsWith('\u2713') ? '#059669' : '#dc2626' }}>{statusMsg}</span>}
                  </div>
                )}
                {doc && (
                  <div style={{ fontSize: 12, color: '#4c1d95', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 6, padding: '3px 8px' }}>
                    {doc.has_document ? ('\uD83D\uDCC4 ' + (doc.type_name || 'Dokument') + (doc.number ? ' ' + doc.number : '')) : '\uD83D\uDCC4 Brak dokumentu sprzeda\u017cy'}
                  </div>
                )}
              </div>
              {/* pole dodawania naszej adnotacji */}
              <div style={{ marginBottom: 14 }}>
                <textarea
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  placeholder={"Dodaj w\u0142asn\u0105 adnotacj\u0119 do tego zam\u00f3wienia\u2026"}
                  rows={2}
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd6fe', resize: 'vertical', fontFamily: 'inherit' }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                  <button
                    onClick={addNote}
                    disabled={saving || !draft.trim()}
                    style={{ fontSize: 13, fontWeight: 600, padding: '7px 16px', borderRadius: 8, border: 'none', background: (saving || !draft.trim()) ? '#c4b5fd' : '#6d28d9', color: '#fff', cursor: (saving || !draft.trim()) ? 'default' : 'pointer' }}
                  >
                    {saving ? '\u23f3 Zapisuj\u0119\u2026' : '\u002b Dodaj adnotacj\u0119'}
                  </button>
                  {saveErr && <span style={{ fontSize: 12, color: '#dc2626' }}>{saveErr}</span>}
                  {syncMsg && <span style={{ fontSize: 12, color: '#059669' }}>{syncMsg}</span>}
                </div>
              </div>

              {loading && <div style={{ fontSize: 13, color: '#6b7280', padding: '20px 0', textAlign: 'center' }}>{'\u0141adowanie\u2026'}</div>}
              {!loading && errMsg && (
                <div style={{ fontSize: 14, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 14px' }}>{errMsg}</div>
              )}
              {!loading && !errMsg && notes && notes.length === 0 && (
                <div style={{ fontSize: 14, color: '#6b7280', padding: '16px 0', textAlign: 'center' }}>{"Brak wiadomo\u015bci. Dodaj pierwsz\u0105 adnotacj\u0119 powy\u017cej."}</div>
              )}
              {!loading && !errMsg && notes && notes.length > 0 && notes.map((n, i) => {
                const isLocal = n.source === 'local';
                return (
                  <div key={n.id || i} style={{ border: '1px solid ' + (isLocal ? '#ddd6fe' : '#e5e7eb'), borderRadius: 8, padding: '10px 12px', marginBottom: 8, background: isLocal ? '#f5f3ff' : '#fff' }}>
                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {isLocal && <span style={{ fontSize: 10, fontWeight: 600, color: '#6d28d9', background: '#ede9fe', padding: '1px 7px', borderRadius: 10 }}>{"\ud83d\udcdd Twoja adnotacja"}</span>}
                      <span>{fmtDate(n.created_at)}</span>
                      {isLocal && n.author_name && <span>{"\u2022 " + n.author_name}</span>}
                    </div>
                    <div style={{ fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{n.comment || '\u2014'}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
